@let w = draft();
@let m = meta();
<div
  #specificContainer
  class="iu-specific-window we"
  [class.we--modal]="isModal"
  [attr.data-cy-specific-window-name]="name"
  (keydown)="onKey($event)">
  <!-- top: title and state, which widget (new / duplicate), editor or library -->
  <header class="we-top">
    <div class="we-head">
      @if (!isModal) {
        <span class="we-head-icon" aria-hidden="true"><i class="fa-solid fa-table-cells-large"></i></span>
      }
      <div class="we-head-text">
        @if (!isModal) {
          <h1 class="we-title">{{ name || ('widgetEditor.title' | translate) }}</h1>
        }
        <span class="we-head-state" [class.we-head-state--dirty]="dirty()" role="status">
          <span class="we-dot" aria-hidden="true"></span>
          @if (!(w?.id > 0)) {
            {{ 'widgetEditor.stateNew' | translate }}
          } @else if (dirty()) {
            {{ 'widgetEditor.unsaved' | translate }}
          } @else {
            {{ 'widgetEditor.stateSaved' | translate }}
          }
        </span>
      </div>
    </div>
    <!-- in a modal the host edits one widget: no switching to another one; not over the library -->
    @if (!isModal && view() === 'editor') {
      <div class="we-head-picker">
        <iu-widget-editor-combo
          class="we-picker"
          [label]="'widgetEditor.widget' | translate"
          [placeholder]="'widgetEditor.searchWidget' | translate"
          [options]="widgetOptions()"
          [value]="currentWidgetOption()"
          (valueChange)="pickWidget($event)" />
        <button type="button" class="we-tool-btn" (click)="newOne()" [title]="'widgetEditor.newHelp' | translate">
          <i class="fa-solid fa-plus" aria-hidden="true"></i>{{ 'widgetEditor.new' | translate }}
        </button>
        <button type="button" class="we-tool-btn" [disabled]="!(w?.id > 0)" (click)="duplicateOne()">
          <i class="fa-regular fa-copy" aria-hidden="true"></i>{{ 'widgetEditor.duplicate' | translate }}
        </button>
      </div>
    }
    @if (isAdmin && !isModal) {
      <div class="we-segment we-views" role="tablist" [attr.aria-label]="'widgetEditor.title' | translate">
        <button type="button" role="tab" class="we-segment-btn" [class.we-segment-btn--on]="view() === 'editor'" [attr.aria-selected]="view() === 'editor'" (click)="showEditor()">
          {{ 'widgetEditor.editorView' | translate }}
        </button>
        <button type="button" role="tab" class="we-segment-btn" [class.we-segment-btn--on]="view() === 'library'" [attr.aria-selected]="view() === 'library'" (click)="showLibrary()">
          {{ 'widgetEditor.libraryView' | translate }}
        </button>
      </div>
    }
  </header>

  <!-- draft set in ngOnInit: the body (and the static preview host) always rendered -->
<div #body class="we-body" [hidden]="view() === 'library'">
    <!-- live preview, as on the dashboard -->
    <section class="we-preview" [attr.aria-label]="'widgetEditor.preview' | translate">
      <div class="we-preview-tools">
        <h2 class="we-kicker">{{ 'widgetEditor.preview' | translate }}</h2>
        <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.previewSize' | translate">
          @for (s of previewSizes; track s) {
            <button type="button" role="radio" class="we-segment-btn we-segment-btn--small" [class.we-segment-btn--on]="previewSize() === s" [attr.aria-checked]="previewSize() === s" (click)="previewSize.set(s)" [title]="'widgetEditor.sizes.' + s | translate">
              {{ 'widgetEditor.sizes.' + s | translate }}
            </button>
          }
        </div>
        <button type="button" class="we-icon-btn we-fold" (click)="previewOpen.set(!previewOpen())" [attr.aria-expanded]="previewOpen()" [title]="'widgetEditor.preview' | translate">
          <i class="fa-solid" [class.fa-chevron-up]="previewOpen()" [class.fa-chevron-down]="!previewOpen()" aria-hidden="true"></i>
        </button>
      </div>
      <div class="we-card we-card--{{ previewSize() }}" [hidden]="!previewOpen()">
        <div class="we-card-head">
          <i [class]="m.icon" aria-hidden="true"></i>
          <div class="we-card-title">
            <strong [class.we-muted]="!w.name">{{ w.name || ('widgetEditor.untitled' | translate) }}</strong>
            @if (w.description) {
              <small>{{ w.description }}</small>
            }
          </div>
          <button type="button" class="we-icon-btn" (click)="refreshPreview()" [title]="'widgetEditor.refreshPreview' | translate" [attr.aria-label]="'widgetEditor.refreshPreview' | translate">
            <i class="fa-solid fa-rotate-right" aria-hidden="true"></i>
          </button>
        </div>
        <div class="we-card-body" (pointerdown)="onPreviewInput()" (keydown)="onPreviewInput()">
          <ng-template #previewHost />
          @if (previewMissing().length) {
            <div class="we-preview-empty">
              <span class="we-preview-empty-icon"><i [class]="m.icon" aria-hidden="true"></i></span>
              <strong>{{ 'widgetEditor.previewMissingCount' | translate: { count: previewMissing().length } }}</strong>
              <span class="we-muted">{{ 'widgetEditor.goToFieldHint' | translate }}</span>
              <ul>
                @for (f of previewMissing(); track f) {
                  <li>
                    <button type="button" class="we-todo-btn" (click)="focusField(f)">
                      <span class="we-step-badge" aria-hidden="true">{{ fieldStep(f) }}</span>
                      <span class="we-todo-label">{{ 'widgetEditor.fields.' + f | translate }}</span>
                      <i class="fa-solid fa-chevron-right" aria-hidden="true"></i>
                    </button>
                  </li>
                }
              </ul>
            </div>
          }
        </div>
      </div>
      <!-- what the widget will show, in a few lines: checked at a glance -->
      <div class="we-will">
        <h2 class="we-kicker">{{ 'widgetEditor.willShow' | translate }}</h2>
        <dl>
          @for (r of willShow(); track r.label) {
            <div class="we-will-row">
              <dt>{{ r.label }}</dt>
              <dd [class.we-muted]="r.missing">{{ r.value }}</dd>
            </div>
          }
        </dl>
      </div>
      <p class="we-note"><i class="fa-solid fa-circle-info" aria-hidden="true"></i>{{ 'widgetEditor.previewNote' | translate }}</p>
    </section>

    <div class="we-form">
      <!-- sections of the form: where you are, what is left to fill, jump to it -->
      <nav class="we-nav" [attr.aria-label]="'widgetEditor.sectionsNav' | translate">
        @for (s of sections(); track s.id; let i = $index; let last = $last) {
          <button
            type="button"
            class="we-nav-item"
            [class.we-nav-item--on]="activeSection() === s.id"
            [class.we-nav-item--todo]="s.todo > 0 && showErrors()"
            [class.we-nav-item--optional]="s.optional"
            [attr.aria-current]="activeSection() === s.id ? 'step' : null"
            [title]="s.todo ? ('widgetEditor.sectionTodo' | translate: { count: s.todo }) : ''"
            (click)="goToSection(s.id)">
            @if (s.done) {
              <span class="we-step we-step--done" aria-hidden="true"><i class="fa-solid fa-check"></i></span>
            } @else {
              <span class="we-step" aria-hidden="true">{{ i + 1 }}</span>
            }
            {{ 'widgetEditor.sections.' + s.label | translate }}
          </button>
          @if (!last) {
            <span class="we-nav-line" aria-hidden="true"></span>
          }
        }
        <span class="we-nav-progress">{{ 'widgetEditor.stepsDone' | translate: { done: stepsDone(), total: stepsRequired() } }}</span>
      </nav>

      <!-- 1. type -->
      <section class="we-section" data-section="type">
        <h2>{{ sectionNumber('type') }} · {{ 'widgetEditor.sections.type' | translate }}</h2>
        <div class="we-type-groups" role="radiogroup" [attr.aria-label]="'widgetEditor.sections.type' | translate">
          @for (g of typeGroups(); track g.key) {
            <div class="we-type-group" [class.we-type-group--wide]="g.key === 'charts'">
              <span class="we-type-group-label">{{ 'widgetEditor.typeGroups.' + g.key | translate }}</span>
              <div class="we-types">
                @for (t of g.items; track t.code) {
                  <button
                    type="button"
                    role="radio"
                    class="we-type"
                    [class.we-type--on]="t.code === w.widgetType"
                    [attr.aria-checked]="t.code === w.widgetType"
                    [attr.tabindex]="t.code === w.widgetType ? 0 : -1"
                    [attr.data-type]="t.code"
                    [title]="'widgetEditor.typeHints.' + t.code | translate"
                    (click)="setType(t)"
                    (keydown)="onTypeKey($event, typeIndex(t))">
                    <i [class]="t.icon" aria-hidden="true"></i>
                    <span>{{ 'widgetEditor.types.' + t.code | translate }}</span>
                    @if (t.code === w.widgetType) {
                      <span class="we-type-check" aria-hidden="true"><i class="fa-solid fa-check"></i></span>
                    }
                  </button>
                }
              </div>
            </div>
          }
        </div>
        <p class="we-type-hint"><i [class]="m.icon" aria-hidden="true"></i>{{ 'widgetEditor.typeHints.' + w.widgetType | translate }}</p>
      </section>

      <!-- 2. name -->
      <section class="we-section" data-section="info">
        <h2>{{ sectionNumber('info') }} · {{ 'widgetEditor.sections.info' | translate }}</h2>
        <div class="we-grid">
          <label class="we-field" [class.we-field--invalid]="error('name')" [class.we-next]="nextField() === 'name'">
            <span>{{ 'widgetEditor.fields.name' | translate }} <b class="we-req" aria-hidden="true">*</b></span>
            <input
              class="p-inputtext we-input"
              data-field="name"
              maxlength="60"
              aria-required="true"
              [attr.aria-invalid]="!!error('name')"
              [value]="w.name ?? ''"
              (input)="update({ name: $any($event.target).value }, 'name')" />
            @if (error('name')) {
              <small class="we-error">{{ error('name')! | translate }}</small>
            } @else if (w.id > 0 && info() && !info()!.baseLanguage) {
              <small class="we-help"><i class="fa-solid fa-language" aria-hidden="true"></i> {{ 'widgetEditor.translationNote' | translate: { language: info()!.language } }}</small>
            }
          </label>
          <label class="we-field">
            <span>{{ 'widgetEditor.fields.description' | translate }}</span>
            <input
              class="p-inputtext we-input"
              maxlength="255"
              [placeholder]="'widgetEditor.descriptionHint' | translate"
              [value]="w.description ?? ''"
              (input)="update({ description: $any($event.target).value })" />
          </label>
        </div>
      </section>

      <!-- 3. data source -->
      <section class="we-section" data-section="source">
        <h2>{{ sectionNumber('source') }} · {{ 'widgetEditor.sections.source' | translate }}</h2>
        @if (savedView()) {
          <p class="we-info">
            <i class="fa-solid fa-filter" aria-hidden="true"></i>
            {{ 'widgetEditor.fromSavedView' | translate: { name: gridPref?.name ?? '' } }}
            @if (windowOption(); as win) {
              <span class="we-muted">{{ win.name }}@if (tabOption(); as tab) { - {{ tab.name }}}</span>
            }
          </p>
          @if (filterPhrases().length) {
            <ul class="we-phrases" [attr.aria-label]="'widgetEditor.filtersInWords' | translate">
              @for (p of filterPhrases(); track $index) {
                <li>
                  <strong>{{ p.label }}</strong> {{ 'widgetEditor.ops.' + p.operator | translate }}
                  @if (p.values.length) {
                    <em>{{ p.values.join(', ') }}</em>
                  }
                </li>
              }
            </ul>
          }
        } @else {
          @if (m.sources.length > 1) {
            <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.sections.source' | translate">
              @for (s of m.sources; track s) {
                <button
                  type="button"
                  role="radio"
                  class="we-segment-btn"
                  [class.we-segment-btn--on]="source() === s"
                  [attr.aria-checked]="source() === s"
                  (click)="setSourceKind(s)">
                  <i [class]="s === 'tab' ? 'fa-solid fa-window-maximize' : 'fa-solid fa-database'" aria-hidden="true"></i>
                  {{ 'widgetEditor.sources.' + s | translate }}
                </button>
              }
            </div>
            <p class="we-help">{{ 'widgetEditor.sourceHints.' + source() | translate }}</p>
          }
          <div class="we-grid">
            @switch (source()) {
              @case ('tab') {
                <iu-widget-editor-combo
                  field="windowId"
                  [class.we-next]="nextField() === 'windowId'"
                  [label]="'widgetEditor.fields.windowId' | translate"
                  [placeholder]="'widgetEditor.searchPlaceholder' | translate"
                  [required]="true"
                  [error]="error('windowId')"
                  [search]="windowSearch"
                  [value]="windowOption()"
                  (valueChange)="pickWindow($event)" />
                <iu-widget-editor-combo
                  field="tabId"
                  [class.we-next]="nextField() === 'tabId'"
                  [label]="'widgetEditor.fields.tabId' | translate"
                  [required]="true"
                  [disabled]="!w.windowId"
                  [error]="error('tabId')"
                  [options]="tabOptions()"
                  [value]="tabOption()"
                  (valueChange)="pickTab($event)" />
              }
              @case ('table') {
                <iu-widget-editor-combo
                  class="we-field--wide"
                  field="tableId"
                  [class.we-next]="nextField() === 'tableId'"
                  [label]="'widgetEditor.fields.tableId' | translate"
                  [placeholder]="'widgetEditor.searchPlaceholder' | translate"
                  [required]="true"
                  [error]="error('tableId')"
                  [search]="tableSearch"
                  [value]="tableOption()"
                  (valueChange)="pickTable($event)" />
              }
              @case ('url') {
                <label class="we-field we-field--wide" [class.we-field--invalid]="error('iframe')" [class.we-next]="nextField() === 'iframe'">
                  <span>{{ 'widgetEditor.fields.iframe' | translate }} <b class="we-req" aria-hidden="true">*</b></span>
                  <input
                    class="p-inputtext we-input"
                    type="url"
                    data-field="iframe"
                    placeholder="https://"
                    aria-required="true"
                    [attr.aria-invalid]="!!error('iframe')"
                    [value]="w.iframe ?? ''"
                    (input)="update({ iframe: $any($event.target).value }, 'iframe')" />
                  @if (error('iframe')) {
                    <small class="we-error">{{ error('iframe')! | translate }}</small>
                  } @else {
                    <small class="we-help">{{ 'widgetEditor.iframeHint' | translate }}</small>
                  }
                </label>
              }
              @case ('none') {
                <iu-widget-editor-combo
                  class="we-field--wide"
                  field="angularClass"
                  [class.we-next]="nextField() === 'angularClass'"
                  [label]="'widgetEditor.fields.angularClass' | translate"
                  [required]="true"
                  [error]="error('angularClass')"
                  [options]="genericComponents()"
                  [value]="genericOption()"
                  (valueChange)="pickGeneric($event)" />
              }
            }
          </div>
          @if (showFilters()) {
            <div class="we-filters">
              <span class="we-label">{{ 'widgetEditor.filters' | translate }}</span>
              <div class="we-filter-box" [class.we-filter-box--empty]="!filterPhrases().length">
                @if (!filterPhrases().length) {
                  <span class="we-filter-empty">{{ 'widgetEditor.noFilterLong' | translate }}</span>
                }
              <iu-universal-filter-standalone-ui
                [columns]="searchColumns"
                [tabId]="w.tabId"
                [setFilterEmitter]="updateFieldUFEmitter"
                [widthChipsContainer]="null"
                [hasScroll]="false"
                (filterChange)="onFilterChange($event)"
                [showFavorite]="false"
                [canFilter]="true"
                [canGroup]="false"
                [canSort]="true" />
              </div>
            </div>
          }
          @if (filterPhrases().length) {
            <ul class="we-phrases" [attr.aria-label]="'widgetEditor.filtersInWords' | translate">
              @for (p of filterPhrases(); track $index) {
                <li>
                  <strong>{{ p.label }}</strong> {{ 'widgetEditor.ops.' + p.operator | translate }}
                  @if (p.values.length) {
                    <em>{{ p.values.join(', ') }}</em>
                  }
                </li>
              }
            </ul>
          }
        }
      </section>

      <!-- 4. values: what is counted or drawn -->
      @if (m.aggregate || m.category) {
        <section class="we-section" data-section="values">
          <h2>{{ sectionNumber('values') }} · {{ (m.family === 'count' ? 'widgetEditor.sections.count' : 'widgetEditor.sections.chart') | translate }}</h2>
          @if (columnsLoading()) {
            <p class="we-help"><i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>{{ 'widgetEditor.loadingColumns' | translate }}</p>
          } @else if (!sourceColumns().length && (w.tabId || w.tableId)) {
            <p class="we-help">{{ 'widgetEditor.noColumns' | translate }}</p>
          }
          @if (m.view) {
            <div class="we-field">
              <span class="we-label">{{ 'widgetEditor.defaultView' | translate }}</span>
              <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.defaultView' | translate">
                @for (v of [CountListViewType.COUNT_VIEW, CountListViewType.LIST_VIEW]; track v) {
                  <button
                    type="button"
                    role="radio"
                    class="we-segment-btn"
                    [class.we-segment-btn--on]="(w.defaultView ?? 'CVT') === v"
                    [attr.aria-checked]="(w.defaultView ?? 'CVT') === v"
                    (click)="update({ defaultView: v })">
                    <i [class]="v === 'CVT' ? 'fa-solid fa-hashtag' : 'fa-solid fa-list'" aria-hidden="true"></i>
                    {{ (v === 'CVT' ? 'widgetEditor.countView' : 'widgetEditor.listView') | translate }}
                  </button>
                }
              </div>
            </div>
          }
          <div class="we-grid">
            @if (m.category) {
              <div class="we-pair">
                <iu-widget-editor-combo
                  field="categoryColumnId"
                  [class.we-next]="nextField() === 'categoryColumnId'"
                  [label]="'widgetEditor.fields.categoryColumnId' | translate"
                  [hint]="(sourceColumns().length ? 'widgetEditor.categoryHint' : 'widgetEditor.pickSourceFirst') | translate"
                  [required]="true"
                  [disabled]="!sourceColumns().length"
                  [error]="error('categoryColumnId')"
                  [options]="columnOptions()"
                  [value]="columnOption(w.categoryColumnId)"
                  (valueChange)="pickColumn('categoryColumnId', $event)" />
                <ng-container *ngTemplateOutlet="sortTpl; context: { field: 'categorySort', value: w.categorySort, disabled: !w.categoryColumnId }" />
              </div>
              <div class="we-pair">
                <iu-widget-editor-combo
                  field="serieColumnId"
                  [class.we-next]="nextField() === 'serieColumnId'"
                  [label]="'widgetEditor.fields.serieColumnId' | translate"
                  [hint]="(!sourceColumns().length ? 'widgetEditor.pickSourceFirst' : m.needsSerie ? 'widgetEditor.serieNeeded' : 'widgetEditor.serieHint') | translate"
                  [required]="m.needsSerie"
                  [disabled]="!sourceColumns().length"
                  [error]="error('serieColumnId')"
                  [options]="columnOptions()"
                  [value]="columnOption(w.serieColumnId)"
                  (valueChange)="pickColumn('serieColumnId', $event)" />
                <ng-container *ngTemplateOutlet="sortTpl; context: { field: 'serieSort', value: w.serieSort, disabled: !w.serieColumnId }" />
              </div>
            }
            @if (m.aggregate) {
              <div class="we-pair">
                <iu-widget-editor-combo
                  field="aggregateColumnId"
                  [class.we-next]="nextField() === 'aggregateColumnId'"
                  [label]="'widgetEditor.fields.aggregateColumnId' | translate"
                  [required]="m.family === 'chart' || w.defaultView !== 'LVT'"
                  [disabled]="!sourceColumns().length"
                  [error]="error('aggregateColumnId')"
                  [hint]="(sourceColumns().length ? 'widgetEditor.valueHint' : 'widgetEditor.pickSourceFirst') | translate"
                  [options]="valueColumnOptions()"
                  [value]="columnOption(w.aggregateColumnId)"
                  (valueChange)="pickColumn('aggregateColumnId', $event)" />
                @if (m.family === 'chart') {
                  <ng-container *ngTemplateOutlet="sortTpl; context: { field: 'aggregateSort', value: w.aggregateSort, disabled: !w.aggregateColumnId }" />
                }
              </div>
              <div class="we-field" [class.we-field--invalid]="error('aggregateOperator')" [class.we-next]="nextField() === 'aggregateOperator'">
                <span class="we-label">{{ 'widgetEditor.fields.aggregateOperator' | translate }}</span>
                <div class="we-segment" role="radiogroup" data-field="aggregateOperator" tabindex="-1" [attr.aria-label]="'widgetEditor.fields.aggregateOperator' | translate">
                  @for (op of operators(); track op) {
                    <button
                      type="button"
                      role="radio"
                      class="we-segment-btn"
                      [class.we-segment-btn--on]="w.aggregateOperator === op"
                      [attr.aria-checked]="w.aggregateOperator === op"
                      [disabled]="!w.aggregateColumnId"
                      (click)="update({ aggregateOperator: op }, 'aggregateOperator')">
                      {{ 'widgetEditor.operators.' + op | translate }}
                    </button>
                  }
                </div>
                @if (error('aggregateOperator')) {
                  <small class="we-error">{{ error('aggregateOperator')! | translate }}</small>
                } @else if (!w.aggregateColumnId) {
                  <small class="we-help">{{ 'widgetEditor.pickValueFirst' | translate }}</small>
                }
              </div>
            }
            <label class="we-field" [class.we-field--invalid]="error('recordLimit')">
              <span>{{ 'widgetEditor.fields.recordLimit' | translate }}</span>
              <input
                class="p-inputtext we-input"
                type="number"
                min="0"
                step="1"
                data-field="recordLimit"
                [placeholder]="'widgetEditor.noLimit' | translate"
                [value]="w.recordLimit || ''"
                (change)="update({ recordLimit: numberValue($any($event.target).value) ?? 0 }, 'recordLimit')" />
              <small class="we-help">{{ (m.family === 'chart' ? 'widgetEditor.limitChartHint' : 'widgetEditor.limitHint') | translate }}</small>
            </label>
          </div>
        </section>
      }

      <!-- 5. display -->
      @if (m.format || m.thresholds || m.family !== 'generic') {
        <section class="we-section" data-section="display">
          <button type="button" class="we-fold-head" [attr.aria-expanded]="displayShown()" (click)="displayOpen.set(!displayShown())">
            <h2>{{ sectionNumber('display') }} · {{ 'widgetEditor.sections.display' | translate }}</h2>
            <i class="fa-solid" [class.fa-chevron-up]="displayShown()" [class.fa-chevron-down]="!displayShown()" aria-hidden="true"></i>
          </button>
          @if (!displayShown()) {
            <button type="button" class="we-summary" (click)="displayOpen.set(true)">
              @for (p of displaySummary(); track $index) {
                <span class="we-summary-item">{{ p }}</span>
              }
              <span class="we-summary-edit">{{ 'widgetEditor.showOptions' | translate }}</span>
            </button>
          } @else {
          @if (m.format) {
            <div class="we-grid we-grid--4">
              <label class="we-field">
                <span>{{ 'widgetEditor.fields.measureUnit' | translate }}</span>
                <input class="p-inputtext we-input" maxlength="20" placeholder="EUR, kg, %..." [value]="w.measureUnit ?? ''" (input)="update({ measureUnit: $any($event.target).value })" />
              </label>
              <label class="we-field" [class.we-field--invalid]="error('precision')">
                <span>{{ 'widgetEditor.fields.precision' | translate }}</span>
                <input class="p-inputtext we-input" type="number" min="0" max="10" step="1" data-field="precision" [value]="w.precision ?? ''" (change)="update({ precision: numberValue($any($event.target).value) ?? 2 }, 'precision')" />
                @if (error('precision')) {
                  <small class="we-error">{{ error('precision')! | translate }}</small>
                }
              </label>
              <label class="we-field">
                <span>{{ 'widgetEditor.fields.multiplier' | translate }}</span>
                <input class="p-inputtext we-input" type="number" step="any" placeholder="1" [value]="w.multiplier ?? ''" (change)="update({ multiplier: numberValue($any($event.target).value) })" />
                <small class="we-help">{{ 'widgetEditor.multiplierHint' | translate }}</small>
              </label>
              <label class="we-check">
                <input type="checkbox" [checked]="w.isRounded" (change)="update({ isRounded: $any($event.target).checked })" />
                <span>{{ 'widgetEditor.fields.isRounded' | translate }}</span>
              </label>
            </div>
            <div class="we-field we-format">
              <span class="we-label">{{ 'widgetEditor.format' | translate }}</span>
              <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.format' | translate">
                @for (f of formats; track f) {
                  <button type="button" role="radio" class="we-segment-btn" [class.we-segment-btn--on]="(display().format ?? 'number') === f" [attr.aria-checked]="(display().format ?? 'number') === f" (click)="setDisplay({ format: f })">
                    {{ 'widgetEditor.formats.' + f | translate }}
                  </button>
                }
                @if (display().format === 'currency') {
                  <input class="p-inputtext we-input we-input--short" maxlength="3" placeholder="EUR" [attr.aria-label]="'widgetEditor.currency' | translate" [value]="display().currency ?? ''" (change)="setDisplay({ currency: $any($event.target).value.trim().toUpperCase() || undefined })" />
                }
              </div>
              <small class="we-help">{{ 'widgetEditor.formatHints.' + (display().format ?? 'number') | translate }}</small>
            </div>
          }
          @if (m.family === 'chart') {
            <h3>{{ 'widgetEditor.chartOptions' | translate }}</h3>
            <div class="we-grid">
              <div class="we-field">
                <span class="we-label">{{ 'widgetEditor.legend' | translate }}</span>
                <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.legend' | translate">
                  @for (l of legends; track l) {
                    <button type="button" role="radio" class="we-segment-btn" [class.we-segment-btn--on]="(display().legend ?? 'auto') === l" [attr.aria-checked]="(display().legend ?? 'auto') === l" (click)="setDisplay({ legend: l })">
                      {{ 'widgetEditor.legends.' + l | translate }}
                    </button>
                  }
                </div>
              </div>
              <div class="we-field we-checks">
                <label class="we-check">
                  <input type="checkbox" [checked]="!!display().showValues" (change)="setDisplay({ showValues: $any($event.target).checked || undefined })" />
                  <span>{{ 'widgetEditor.showValues' | translate }}</span>
                </label>
                @if (canStack()) {
                  <label class="we-check" [title]="'widgetEditor.axisLabelsHint' | translate">
                    <input type="checkbox" [checked]="!!display().axisLabels" (change)="setDisplay({ axisLabels: $any($event.target).checked || undefined })" />
                    <span>{{ 'widgetEditor.axisLabels' | translate }}</span>
                  </label>
                }
                @if (canStack()) {
                  <label class="we-check" [title]="'widgetEditor.stackedHint' | translate">
                    <input type="checkbox" [checked]="!!display().stacked" [disabled]="!w.serieColumnId" (change)="setDisplay({ stacked: $any($event.target).checked || undefined })" />
                    <span>{{ 'widgetEditor.stacked' | translate }}</span>
                  </label>
                }
                @if (w.widgetType === 'LIC') {
                  <label class="we-check">
                    <input type="checkbox" [checked]="!!display().cumulative" (change)="setDisplay({ cumulative: $any($event.target).checked || undefined })" />
                    <span>{{ 'widgetEditor.cumulative' | translate }}</span>
                  </label>
                }
              </div>
              @if (cartesian()) {
                <label class="we-field">
                  <span>{{ 'widgetEditor.xTitle' | translate }}</span>
                  <input class="p-inputtext we-input" maxlength="60" [value]="display().xTitle ?? ''" (change)="setDisplay({ xTitle: $any($event.target).value || undefined })" />
                </label>
                <label class="we-field">
                  <span>{{ 'widgetEditor.yTitle' | translate }}</span>
                  <input class="p-inputtext we-input" maxlength="60" [value]="display().yTitle ?? ''" (change)="setDisplay({ yTitle: $any($event.target).value || undefined })" />
                </label>
              }
            </div>
          }
          @if (m.thresholds) {
            <h3>{{ 'widgetEditor.thresholds' | translate }}</h3>
            <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.direction' | translate">
              @for (d of ['higherWorse', 'higherBetter']; track d) {
                <button type="button" role="radio" class="we-segment-btn" [class.we-segment-btn--on]="direction() === d" [attr.aria-checked]="direction() === d" (click)="setDisplay({ alertDirection: $any(d) })">
                  <i class="fa-solid" [class.fa-arrow-trend-up]="d === 'higherBetter'" [class.fa-arrow-trend-down]="d === 'higherWorse'" aria-hidden="true"></i>
                  {{ 'widgetEditor.directions.' + d | translate }}
                </button>
              }
            </div>
            <p class="we-help">{{ (direction() === 'higherBetter' ? 'widgetEditor.thresholdsHintBetter' : 'widgetEditor.thresholdsHint') | translate }}</p>
            <div class="we-levels">
              <label class="we-field we-level-box">
                <span class="we-label">{{ 'widgetEditor.normalLevel' | translate }}</span>
                <span class="we-level">
                  <input class="p-inputtext we-input" type="number" step="any" data-field="normalLevel" [value]="w.normalLevel ?? ''" (change)="update({ normalLevel: numberValue($any($event.target).value) ?? 0 })" />
                  <input type="color" class="we-color" [value]="w.normalColor || '#63a856'" (input)="update({ normalColor: $any($event.target).value })" [attr.aria-label]="'widgetEditor.normalColor' | translate" />
                </span>
              </label>
              <div class="we-field we-level-box we-level-box--between">
                <span class="we-label">{{ 'widgetEditor.warningColor' | translate }}</span>
                <span class="we-level">
                  <span class="we-level-text">{{ 'widgetEditor.between' | translate }}</span>
                  <input type="color" class="we-color" [value]="w.warningColor || '#b4892d'" (input)="update({ warningColor: $any($event.target).value })" [attr.aria-label]="'widgetEditor.warningColor' | translate" />
                </span>
              </div>
              <label class="we-field we-level-box">
                <span class="we-label">{{ 'widgetEditor.alertLevel' | translate }}</span>
                <span class="we-level">
                  <input class="p-inputtext we-input" type="number" step="any" [value]="w.alertLevel ?? ''" (change)="update({ alertLevel: numberValue($any($event.target).value) ?? 0 })" />
                  <input type="color" class="we-color" [value]="w.alertColor || '#9b0015'" (input)="update({ alertColor: $any($event.target).value })" [attr.aria-label]="'widgetEditor.alertColor' | translate" />
                </span>
              </label>
            </div>
            @if (error('alertLevel')) {
              <small class="we-error" data-field="alertLevel" tabindex="-1">{{ error('alertLevel')! | translate }}</small>
            }
            @if (levelRanges().length) {
              <ul class="we-rangebar" [attr.aria-label]="'widgetEditor.ranges' | translate">
                @for (r of levelRanges(); track $index) {
                  <li [style.background]="r.color" [title]="'widgetEditor.states.' + r.state | translate">
                    {{ r.key | translate: r.params }}
                  </li>
                }
              </ul>
            } @else {
              <p class="we-help">{{ 'widgetEditor.noLevels' | translate }}</p>
            }
          }
          @if (m.family !== 'generic') {
            <div class="we-field we-refresh" [class.we-field--invalid]="error('refreshRate')">
              <span class="we-label">{{ 'widgetEditor.fields.refreshRate' | translate }}</span>
              <div class="we-segment" role="radiogroup" [attr.aria-label]="'widgetEditor.fields.refreshRate' | translate">
                @for (s of refreshPresets; track s) {
                  <button type="button" role="radio" class="we-segment-btn" [class.we-segment-btn--on]="(w.refreshRate || 0) === s" [attr.aria-checked]="(w.refreshRate || 0) === s" (click)="setRefresh(s)">
                    {{ 'widgetEditor.refresh.' + s | translate }}
                  </button>
                }
                <input
                  class="p-inputtext we-input we-input--short"
                  type="number"
                  min="0"
                  step="1"
                  data-field="refreshRate"
                  [value]="w.refreshRate || ''"
                  [placeholder]="'widgetEditor.seconds' | translate"
                  [attr.aria-label]="'widgetEditor.seconds' | translate"
                  (change)="setRefresh(numberValue($any($event.target).value))" />
              </div>
            </div>
          }
          }
        </section>
      }

      <!-- 6. access (administrators) -->
      @if (isAdmin) {
        <section class="we-section" data-section="access">
          <h2>{{ sectionNumber('access') }} · {{ 'widgetEditor.sections.access' | translate }}</h2>
          <p class="we-help">{{ (selectedRoles().size ? 'widgetEditor.sharedHint' : 'widgetEditor.personalHint') | translate }}</p>
          @if (roleOptions().length > 8) {
            <input class="p-inputtext we-input we-role-filter" type="search" [placeholder]="'widgetEditor.filterRoles' | translate" [value]="roleFilter()" (input)="roleFilter.set($any($event.target).value)" />
          }
          <div class="we-roles" role="group" [attr.aria-label]="'widgetEditor.roleAccess' | translate">
            @for (r of shownRoles(); track r.id) {
              <label class="we-check">
                <input type="checkbox" [checked]="selectedRoles().has(r.id)" (change)="toggleRole(r.id, $any($event.target).checked)" />
                <span>{{ r.name }}</span>
              </label>
            } @empty {
              <span class="we-muted">{{ 'widgetEditor.noRoles' | translate }}</span>
            }
          </div>
          @if (rolesWithoutAccess().length) {
            <p class="we-warn" role="status">
              <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>
              {{ 'widgetEditor.rolesWithoutAccess' | translate }}
              @for (id of rolesWithoutAccess(); track id; let last = $last) {
                <strong>{{ roleName(id) }}</strong>@if (!last) {, }
              }
            </p>
          }
        </section>
      }
    </div>
  </div>

  <!-- administrators: every widget of the client -->
  @if (view() === 'library') {
    <section class="we-library" [attr.aria-label]="'widgetEditor.libraryView' | translate">
      <div class="we-library-tools">
        <input class="p-inputtext we-input we-library-filter" type="search" [placeholder]="'widgetEditor.libraryFilter' | translate" [value]="libraryFilter()" (input)="libraryFilter.set($any($event.target).value)" />
        <button type="button" class="we-icon-btn" (click)="loadLibrary()" [title]="'widgetEditor.refreshList' | translate" [attr.aria-label]="'widgetEditor.refreshList' | translate">
          <i class="fa-solid fa-rotate-right" [class.fa-spin]="libraryLoading()" aria-hidden="true"></i>
        </button>
        <span class="we-spacer"></span>
        <button type="button" class="p-button p-button-alert we-btn" [disabled]="!shownSelection()" (click)="deleteSelected()">
          <i class="fa-regular fa-trash-can" aria-hidden="true"></i>{{ 'widgetEditor.deleteSelected' | translate: { count: shownSelection() } }}
        </button>
      </div>
      <div class="we-table-wrap">
        <table class="we-table">
          <thead>
            <tr>
              <th scope="col" class="we-col-check">
                <input type="checkbox" [checked]="shownLibrary().length && shownSelection() === shownLibrary().length" (change)="toggleLibraryAll($any($event.target).checked)" [attr.aria-label]="'widgetEditor.selectAll' | translate" />
              </th>
              <th scope="col" class="we-col-name">{{ 'widgetEditor.fields.name' | translate }}</th>
              <th scope="col" class="we-col-type">{{ 'widgetEditor.sections.type' | translate }}</th>
              <th scope="col">{{ 'widgetEditor.owner' | translate }}</th>
              <th scope="col" class="we-col-num">{{ 'widgetEditor.dashboards' | translate }}</th>
              <th scope="col" class="we-col-date">{{ 'widgetEditor.updated' | translate }}</th>
            </tr>
          </thead>
          <tbody>
            @for (r of shownLibrary(); track r.id) {
              <tr [class.we-row--on]="librarySelection().has(r.id)">
                <td class="we-col-check"><input type="checkbox" [checked]="librarySelection().has(r.id)" (change)="toggleLibraryRow(r.id, $any($event.target).checked)" [attr.aria-label]="r.name" /></td>
                <td [title]="r.name"><button type="button" class="we-link" (click)="openFromLibrary(r.id)">{{ r.name || '#' + r.id }}</button></td>
                <td><i [class]="typeIcon(r.widgetType)" aria-hidden="true"></i> {{ typeLabel(r.widgetType) }}</td>
                <td [title]="r.sharedRoles.length ? r.sharedRoles.join(', ') : (r.userName || '') + ' ' + (r.roleName || '')">
                  @if (r.sharedRoles.length) {
                    <!-- shared: the number of roles, then their names cut to the column (all in the tooltip) -->
                    <span class="we-shared">
                      <span class="we-shared-count">{{ r.sharedRoles.length }}</span>
                      <span class="we-shared-names">{{ r.sharedRoles.join(', ') }}</span>
                    </span>
                  } @else {
                    {{ r.userName || '-' }} <small class="we-muted">{{ r.roleName }}</small>
                  }
                </td>
                <td class="we-col-num" [title]="'widgetEditor.dashboardItems' | translate: { count: r.dashboardItems }">{{ r.dashboards }}</td>
                <td>{{ r.updated ? (r.updated | date: 'short') : '' }}</td>
              </tr>
            } @empty {
              <tr>
                <td colspan="6" class="we-muted">{{ (libraryLoading() ? 'widgetEditor.loading' : 'widgetEditor.noWidgets') | translate }}</td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    </section>
  }

  <!-- actions, always visible -->
  <footer class="we-foot" [hidden]="view() === 'library'">
    @if (errorCount()) {
      <!-- what is left to fill, from the start (red once a save was tried); goes to the first one -->
      <button type="button" class="we-errors" [class.we-errors--alert]="showErrors()" (click)="focusFirstError()">
        <i class="fa-solid fa-triangle-exclamation" aria-hidden="true"></i>{{ 'widgetEditor.toComplete' | translate: { count: errorCount() } }}
      </button>
    }
    <span class="we-spacer"></span>
    <span class="we-shortcut">{{ 'widgetEditor.shortcutHint' | translate }}</span>
    @if (w.id > 0) {
      <button type="button" class="we-tool-btn we-tool-btn--danger" [disabled]="saving()" (click)="deleteOne()">
        <i class="fa-regular fa-trash-can" aria-hidden="true"></i>{{ 'widgetEditor.deleteWidget' | translate }}
      </button>
    }
    @if (isModal) {
      <button type="button" class="we-tool-btn" (click)="close()">{{ 'widgetEditor.close' | translate }}</button>
    }
    @if (!isModal && dashboardTabs().length) {
      <div class="we-add">
        <div class="we-add-select">
          <label class="we-add-label" for="we-add-tab">{{ 'widgetEditor.addTo' | translate }}</label>
          <select id="we-add-tab" [title]="'widgetEditor.dashboardTab' | translate" (change)="addTabId.set(+$any($event.target).value)">
            @for (c of dashboardTabs(); track c.id) {
              <option [value]="c.id" [selected]="c.id === addTabId()">{{ c.name }}</option>
            }
          </select>
        </div>
        <button type="button" class="we-tool-btn" [disabled]="saving()" (click)="saveAndAdd()">
          <i class="fa-solid fa-table-cells-large" aria-hidden="true"></i>{{ (w.id > 0 ? 'widgetEditor.saveAndAdd' : 'widgetEditor.createAndAdd') | translate }}
        </button>
      </div>
    }
    <button type="button" class="we-primary-btn" [disabled]="saving()" (click)="save()" [title]="'widgetEditor.saveShortcut' | translate">
      @if (saving()) {
        <i class="fa-solid fa-circle-notch fa-spin" aria-hidden="true"></i>
      }
      {{ (w.id > 0 ? 'widgetEditor.saveWidget' : 'widgetEditor.addWidget') | translate }}
    </button>
  </footer>
</div>

<!-- sort of a column: none, ascending, descending -->
<ng-template #sortTpl let-field="field" let-value="value" let-disabled="disabled">
  <label class="we-field we-sort">
    <span>{{ 'widgetEditor.sort' | translate }}</span>
    <select class="p-inputtext we-input" [disabled]="disabled" (change)="setSort(field, $any($event.target).value)">
      <option value="" [selected]="!value">{{ 'widgetEditor.sorts.none' | translate }}</option>
      <option value="ASC" [selected]="value === 'ASC'">{{ 'widgetEditor.sorts.ASC' | translate }}</option>
      <option value="DSC" [selected]="value === 'DSC'">{{ 'widgetEditor.sorts.DSC' | translate }}</option>
    </select>
  </label>
</ng-template>
